import React, { useMemo, useState } from "react";
import { ClipboardList, Loader2, X } from "lucide-react";
import {
  COIN_POOL_ORDER_STATUS_LABEL,
  COIN_SIDE_LABEL,
  canCancelCoinPoolOrder,
  formatCoinPoolCoin,
  orderFillPercent,
  orderStatusTone,
} from "../../utils/coinPoolHelpers";
import OrderFillBar from "./OrderFillBar";

const formatTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const CoinPoolOrdersList = ({
  round,
  userId,
  actionLoading,
  onCancelOrder,
  embedded = false,
  title = "Orders",
}) => {
  const [cancellingId, setCancellingId] = useState(null);

  const orders = useMemo(() => {
    const all = round?.orders || [];
    return [...all].sort((a, b) => {
      const aMine = userId && String(a.userId) === String(userId) ? 1 : 0;
      const bMine = userId && String(b.userId) === String(userId) ? 1 : 0;
      if (aMine !== bMine) return bMine - aMine;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [round?.orders, userId]);

  const canCancelRound = round?.status === "waiting";

  const handleCancel = async (order, event) => {
    event?.stopPropagation();
    if (!onCancelOrder || cancellingId || actionLoading) return;
    setCancellingId(order.id);
    try {
      await onCancelOrder(order.id);
    } finally {
      setCancellingId(null);
    }
  };

  const body =
    orders.length === 0 ? (
      <div className="bcoin-ob-orders-empty">
        <p>No orders this round.</p>
      </div>
    ) : (
      <ul className="bcoin-ob-order-list">
        {orders.map((order) => {
          const tone = orderStatusTone(order.status);
          const isMine = userId && String(order.userId) === String(userId);
          const cancellable =
            isMine && canCancelRound && canCancelCoinPoolOrder(order);
          const isCancelling = cancellingId === order.id;
          const fillPct = orderFillPercent(order);
          const playerName = order.name || "Player";
          const side = order.side === "tail" ? "tail" : "head";
          const showProgress = order.status === "partial";

          return (
            <li
              key={order.id}
              className={`bcoin-ob-order-item side-${side} status-${tone}${isMine ? " is-mine" : ""}`}
            >
              <div className="bcoin-ob-order-item-main">
                <span className={`bcoin-ob-order-side ${side}`}>
                  {COIN_SIDE_LABEL[side]}
                </span>
                <span className="bcoin-ob-order-who">{isMine ? "You" : playerName}</span>
                <span className="bcoin-ob-order-amt tabular-nums">
                  {formatCoinPoolCoin(order.amount)}
                </span>
                <span className={`bcoin-ob-status-pill ${tone}`}>
                  {COIN_POOL_ORDER_STATUS_LABEL[order.status] || order.status}
                </span>
                <span className="bcoin-ob-order-time">{formatTime(order.createdAt)}</span>
                {cancellable ? (
                  <button
                    type="button"
                    className="bcoin-ob-cancel-btn icon"
                    disabled={Boolean(cancellingId) || actionLoading}
                    aria-busy={isCancelling}
                    aria-label={
                      order.remainingAmount > 0
                        ? `Cancel order, refund ${formatCoinPoolCoin(order.remainingAmount)}`
                        : "Cancel order"
                    }
                    title="Cancel"
                    onClick={(e) => handleCancel(order, e)}
                  >
                    {isCancelling ? (
                      <Loader2 size={13} className="bplay-spin" aria-hidden />
                    ) : (
                      <X size={13} strokeWidth={2.5} aria-hidden />
                    )}
                  </button>
                ) : null}
              </div>

              {showProgress ? (
                <div className="bcoin-ob-order-progress">
                  <OrderFillBar order={order} className="in-card" />
                  <span className="bcoin-ob-order-fill-pct tabular-nums">{fillPct}%</span>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    );

  if (embedded) {
    return <div className="bcoin-ob-orders-embedded">{body}</div>;
  }

  return (
    <section className="bcoin-panel-section bcoin-ob-orders-section">
      <header className="bcoin-panel-section-head">
        <span className="bcoin-panel-section-title">
          <ClipboardList size={13} strokeWidth={2.5} />
          {title}
        </span>
        {orders.length > 0 ? (
          <span className="bcoin-panel-section-count">{orders.length}</span>
        ) : null}
      </header>
      {body}
    </section>
  );
};

export default CoinPoolOrdersList;
